import React from 'react'

import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'

import formatCurrency from './Formatting'
import { BudgetStatus } from './Types'

function Figure({ label, value, colour }: { label: string, value: string, colour?: string }) {
    return (
        <Box>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="h6" sx={{ color: colour, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
        </Box>
    )
}

const kronor = (amount: number) => `${formatCurrency(amount)} kr`

/**
 * Two scopes, one card, each labelled with what it covers.
 *
 * The top half is the kostnadsställe for the verksamhetsår, which is the only level a budget is set
 * at. It appears as soon as a kostnadsställe is chosen: it needs no project, and requiring one
 * before showing it made the reader pick something irrelevant to see their own section's figures.
 *
 * The bottom half is the chosen project, and shows a result and nothing else. Nothing is budgeted
 * per project, so any figure placed beside it would invite a comparison that does not exist.
 *
 * With no budget entered the top half says so. Showing nothing would read as a bug, and showing a
 * zero would be a claim nobody made.
 */
export default function BudgetBanner({ status, costCenterItemName, project }: {
    status: BudgetStatus,
    costCenterItemName: string,
    /** The chosen project and its result for the same year, once one is chosen. */
    project: { name: string, result: number } | null,
}) {
    const { budget, result, fiscal_year_label: year } = status
    const remaining = budget === null ? null : result - budget

    return (
        <Card variant="outlined" sx={{ p: 2, width: '100%' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {costCenterItemName} · hela kostnadsstället · {year}
            </Typography>
            <Box sx={{ display: 'flex', flexFlow: 'row wrap', gap: '2.5rem' }}>
                <Figure
                    label="Budgeterat resultat"
                    value={budget === null ? 'Ingen budget satt' : kronor(budget)}
                    colour={budget === null ? 'text.secondary' : undefined}
                />
                <Figure label="Resultat hittills" value={kronor(result)}/>
                {remaining !== null && (
                    <Figure
                        // Signed against the budget, so the sign carries the news on its own: above
                        // budget is better than planned whether the plan was a surplus or a deficit.
                        label={remaining >= 0 ? 'Över budget' : 'Under budget'}
                        value={kronor(Math.abs(remaining))}
                        colour={remaining >= 0 ? 'success.main' : 'warning.main'}
                    />
                )}
            </Box>

            {project !== null && <>
                <Divider sx={{ my: 2 }}/>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {project.name} · {year}
                </Typography>
                <Box sx={{ display: 'flex', flexFlow: 'row wrap', gap: '2.5rem' }}>
                    <Figure label="Resultat hittills" value={kronor(project.result)}/>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Summan av tabellerna nedan.
                </Typography>
            </>}
        </Card>
    )
}
